/**
 * Perf Logger (debug-only)
 * Lightweight performance logging utilities for diagnosing UI bottlenecks.
 */

type PerfMeta = Record<string, unknown>;
type PerfGlobalRoot = typeof globalThis & {
    __DEBUG_MODAL_PERF__?: boolean;
};

interface PerfEvent extends PerfMeta {
    label: string;
    t: number;
    dt: number;
}

function now(): number {
    try {
        if (typeof performance !== "undefined" && typeof performance.now === "function") {
            return performance.now();
        }
    } catch (_error) {
        // ignore
    }
    return Date.now();
}

function isEnabled(): boolean {
    const globalRoot = (typeof globalThis !== "undefined" ? globalThis : {}) as PerfGlobalRoot;

    try {
        if (Reflect.get(globalRoot, "__DEBUG_MODAL_PERF__") === true) {
            return true;
        }
    } catch (_error) {
        // ignore
    }

    try {
        const loc = Reflect.get(globalRoot, "location");
        if (loc && typeof loc === "object") {
            const search = Reflect.get(loc, "search");
            if (typeof search === "string" && search !== "") {
                const params = new URLSearchParams(search);
                const value = params.get("debugModalPerf");
                if (value === "1" || value === "true" || value === "yes" || value === "on") return true;
            }
        }
    } catch (_error) {
        // ignore
    }

    try {
        const localStorageLike = Reflect.get(globalRoot, "localStorage");
        if (localStorageLike && typeof localStorageLike === "object") {
            const getItem = Reflect.get(localStorageLike, "getItem");
            if (typeof getItem === "function") {
                const value = getItem.call(localStorageLike, "DEBUG_MODAL_PERF");
                if (value === "1" || value === "true" || value === "yes" || value === "on") return true;
            }
        }
    } catch (_error) {
        // ignore
    }

    return false;
}

class PerfSession {
    public name: string;

    public meta: PerfMeta;

    public t0: number;

    public events: PerfEvent[];

    public ended: boolean;

    private _autoEndTimer: ReturnType<typeof setTimeout> | null;

    public constructor(name: string, meta: PerfMeta = {}) {
        this.name = name;
        this.meta = meta;
        this.t0 = now();
        this.events = [];
        this.ended = false;
        this._autoEndTimer = null;

        this.mark("start");
    }

    public mark(label: string, data: PerfMeta = {}): void {
        if (this.ended) return;
        const time = now();
        this.events.push({
            label,
            t: time,
            dt: Math.round((time - this.t0) * 1000) / 1000,
            ...data,
        });
    }

    public autoEndAfter(ms: number): void {
        if (this.ended) return;
        if (this._autoEndTimer !== null) {
            clearTimeout(this._autoEndTimer);
        }
        this._autoEndTimer = setTimeout(() => {
            this.end("auto-end");
        }, ms);
    }

    public end(reason = "end"): void {
        if (this.ended) return;
        this.ended = true;

        if (this._autoEndTimer !== null) {
            clearTimeout(this._autoEndTimer);
            this._autoEndTimer = null;
        }

        const total = Math.round((now() - this.t0) * 1000) / 1000;

        try {
            const title = `[ModalPerf] ${this.name} ${total}ms (${reason})`;
            console.groupCollapsed(title);
            if (Object.keys(this.meta).length > 0) {
                console.log("meta", this.meta);
            }
            console.table(
                this.events.map((event, index) => ({
                    i: index,
                    label: event.label,
                    dt_ms: event.dt,
                    ...Object.fromEntries(Object.entries(event).filter(([key]) => !["label", "t", "dt"].includes(key))),
                })),
            );
            console.groupEnd();
        } catch (_error) {
            // ignore
        }
    }
}

class PerfLogger {
    private _enabled: boolean | null;

    private _active: PerfSession | null;

    private _observerStarted: boolean;

    public constructor() {
        this._enabled = null;
        this._active = null;
        this._observerStarted = false;
    }

    public enabled(): boolean {
        if (this._enabled === true) return true;

        const enabledNow = isEnabled();
        if (enabledNow) this._enabled = true;
        return enabledNow;
    }

    public startSession(name: string, meta: PerfMeta = {}): PerfSession | null {
        if (!this.enabled()) return null;

        const session = new PerfSession(name, meta);
        this._active = session;
        this._ensureLongTaskObserver();
        return session;
    }

    public activeSession(): PerfSession | null {
        if (!this.enabled()) return null;
        return this._active;
    }

    public clearSession(): void {
        this._active = null;
    }

    private _ensureLongTaskObserver(): void {
        if (this._observerStarted) return;
        this._observerStarted = true;

        try {
            if (typeof PerformanceObserver === "undefined") return;
            const observer = new PerformanceObserver((list) => {
                const session = this._active;
                if (!session || session.ended) return;

                for (const entry of list.getEntries()) {
                    if (!entry) continue;
                    if (typeof entry.duration === "number" && entry.duration >= 50) {
                        session.mark("longtask", {
                            duration_ms: Math.round(entry.duration * 1000) / 1000,
                            name: entry.name || "longtask",
                        });
                    }
                }
            });

            observer.observe({ entryTypes: ["longtask"] });
        } catch (_error) {
            // ignore
        }
    }
}

const perfLogger = new PerfLogger();

export { perfLogger, PerfLogger, PerfSession };
